"use client";

import { useSubscription } from "@/components/subscription-provider";
import { Button } from "@/components/ui/button";
import { canAccessArticle, getArticleAudience } from "@/lib/article-access";
import { useTranslation } from "@/i18n";
import PublisherConfig from "@/configs/publisher.json";
import { OPEN_SIGNIN_EVENT } from "@/lib/open-signin";
import { isPaidEnabled } from "@/lib/paid-plans";
import { Mail } from "lucide-react";
import ContentWrapper from "./content";

export function ArticleBody({ article, content }: { article: any; content: string }) {
  const { t } = useTranslation();
  const { isLoading, isSiteOwner, isSubscribed, isPaidSubscriber } = useSubscription();
  const locked = !canAccessArticle(article, { isLoading, isSiteOwner, isSubscribed, isPaidSubscriber });
  const audience = getArticleAudience(article);

  if (!locked) {
    return (
      <section className="mt-8 max-w-none">
        <ContentWrapper content={content} />
      </section>
    );
  }

  const supporterOnly = audience === "paid_subscribers";
  const supportUnavailable = supporterOnly && !isPaidEnabled();
  const descriptionKey = supportUnavailable
    ? "paywall.supportUnavailable"
    : supporterOnly
      ? "paywall.supportersDescription"
      : "paywall.subscribersDescription";

  return (
    <section className="mt-8 max-w-none relative">
      <div className="pointer-events-none select-none blur-[8px] max-h-72 overflow-hidden">
        <ContentWrapper content={content} />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 bg-linear-to-b from-background/10 via-background/70 to-background">
        <p
          className="text-sm font-semibold text-foreground mb-2"
          data-i18n={supporterOnly ? "paywall.supportersTitle" : "paywall.subscribersTitle"}
        >
          {t(supporterOnly ? "paywall.supportersTitle" : "paywall.subscribersTitle")}
        </p>
        <p className="text-xs text-muted-foreground mb-3 text-center" data-i18n={descriptionKey}>
          {t(descriptionKey, { name: PublisherConfig.profile.name || PublisherConfig.username })}
        </p>
        {!supportUnavailable && (
          <Button
            type="button"
            size="sm"
            className="cursor-pointer shadow-md"
            onClick={() => window.dispatchEvent(new CustomEvent(supporterOnly ? "open-support-modal" : "open-subscribe-modal"))}
            data-i18n={supporterOnly ? "subscribe.becomeSupporter" : "subscribe.subscribe"}
          >
            <Mail className="w-3.5 h-3.5" />
            {t(supporterOnly ? "subscribe.becomeSupporter" : "subscribe.subscribe")}
          </Button>
        )}
        <button
          type="button"
          className="text-xs underline mt-3"
          onClick={() => window.dispatchEvent(new CustomEvent(OPEN_SIGNIN_EVENT))}
          data-i18n={supporterOnly ? "paywall.alreadySupporter" : "paywall.alreadySubscriber"}
        >
          {t(supporterOnly ? "paywall.alreadySupporter" : "paywall.alreadySubscriber")}
        </button>
      </div>
    </section>
  );
}
